import * as Sentry from '@sentry/vue';
import { useLogger } from '@/composables/use-logger';
import { useSub } from '@/composables/use-token';
import { ApiOrganizationNotSelectedError } from '@/errors';

const ORGANIZATION_POLL_INTERVAL_MS = 200;
// グロナビの global-menu fetch timeout より長く待つ
export const ORGANIZATION_WAIT_TIMEOUT_MS = 12_000;

export async function useOrganizationValue(): Promise<string | undefined> {
  const url = new URL(location.href);
  let env: string;
  if (url.host.includes('local')) {
    env = 'local';
  } else if (url.host.includes('dev')) {
    env = 'dev';
  } else if (url.host.includes('stg')) {
    env = 'stg';
  } else {
    env = 'prod';
  }
  const sub = await useSub();
  if (!sub) return undefined;
  const key = `organization:${env}-${sub}`;
  return document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${key}=`))
    ?.split('=')[1];
}

let pendingWait: Promise<string> | null = null;

let timeoutReported = false;

// 診断の失敗で ApiOrganizationNotSelectedError を潰さない
async function logWaitTimeout(): Promise<void> {
  let hasSub = false;
  try {
    // sub 未確定(auth0)と Cookie 不在を切り分けるため、sub の有無だけ残す
    hasSub = Boolean(await useSub());
  } catch {
    // 診断は best effort
  }
  try {
    useLogger().warn({ hasSub }, '[organization] wait timed out');
  } catch {
    // 診断は best effort
  }
  // logger は console 止まりで本番では見えないため Sentry にも送る。ノイズ抑止でページロード 1 回
  if (timeoutReported) return;
  timeoutReported = true;
  try {
    Sentry.captureMessage('organization cookie wait timed out', {
      level: 'warning',
      extra: { hasSub },
    });
  } catch {
    // 診断は best effort
  }
}

async function waitForOrganization(): Promise<string> {
  const deadline = Date.now() + ORGANIZATION_WAIT_TIMEOUT_MS;
  for (;;) {
    const organizationId = await useOrganizationValue();
    if (organizationId) return organizationId;
    if (Date.now() >= deadline) {
      await logWaitTimeout();
      throw new ApiOrganizationNotSelectedError();
    }
    await new Promise((resolve) => setTimeout(resolve, ORGANIZATION_POLL_INTERVAL_MS));
  }
}

/**
 * 組織 Cookie が書かれるまで待って組織 ID を返す。取れなければ送信させず throw する。
 * Cookie はグロナビの mount 後に書かれるため、API 呼び出しが先に走り得る。
 */
export function requireOrganizationValue(): Promise<string> {
  if (!pendingWait) {
    pendingWait = waitForOrganization().finally(() => {
      pendingWait = null;
    });
  }
  return pendingWait;
}
